/**
 * Gestion › Enfants › Consulter : chercher un enfant, voir une semaine, lire
 * ce qu'il faut savoir.
 *
 * ## Refait le 26/10/2026, sur le modèle des écrans de présences
 *
 * Retour d'Éclosion : « les fiches ne sont pas actualisées, la recherche est
 * fastidieuse ; les présences des anims sont beaucoup plus fonctionnelles
 * pour avoir les infos sur les enfants ». L'écran lisait les anciennes
 * colonnes (case photo, « sait nager »), gardait la fiche chargée dans une
 * fenêtre, et demandait pour trouver un enfant de taper, choisir dans une
 * liste, puis cliquer sur un œil — ou quatre listes en cascade pour voir une
 * semaine.
 *
 * Il a désormais :
 *
 * - **une recherche** sur l'enfant, le parent ou l'adresse électronique, qui
 *   ignore les accents (« lea » trouve Léa) et dont les résultats s'ouvrent
 *   d'un clic ;
 * - **une semaine d'un centre**, proposée comme dans Présences, avec
 *   « Enfants | À savoir » ;
 * - **la fiche** relue à chaque ouverture, dans le modèle du parent
 *   (`depuisLignes`), avec ce qu'il faut savoir en tête.
 *
 * Ici, les règles sans la base.
 */

import { normaliser } from '../../../../lib/catalogue'
import { estSansImportance } from '../../../../lib/presences/essentiels'
import { PHRASE_NATATION, type Fiche } from '../../../../components/forms/kid/fiche'

/* ------------------------------------------------------------------------ */
/*  La recherche                                                             */
/* ------------------------------------------------------------------------ */

export type Recherche =
  | { type: 'aucune' }
  | { type: 'email'; texte: string }
  | { type: 'mots'; mots: string[] }

/**
 * Ce que la recherche demande. Deux caractères au moins ; une arobase en fait
 * une recherche d'adresse électronique, sinon chaque mot doit se retrouver
 * dans le prénom, le nom, ou le nom du parent.
 */
export function lireRecherche(brut: string): Recherche {
  const t = brut.trim()
  if (t.length < 2) return { type: 'aucune' }
  if (t.includes('@')) return { type: 'email', texte: t.toLowerCase().replace(/[%_,()"\\]/g, '') }
  const mots = normaliser(t).split(' ').filter(Boolean)
  return mots.length > 0 ? { type: 'mots', mots } : { type: 'aucune' }
}

/**
 * Le motif `ilike` d'un mot, tolérant aux accents : la base compare les
 * lettres telles quelles, et « lea » ne trouverait pas « Léa ». Les voyelles
 * et le « c » deviennent un joker d'un caractère (`_`) ; la liste revenue est
 * ensuite filtrée sans accents (`correspond`), ce qui écarte les faux amis
 * (« lia » pour « lea »). Les mots courts restent tels quels : un joker sur
 * deux lettres rendrait trop de noms.
 */
export function motifIlike(mot: string): string {
  const propre = mot.replace(/[^a-z0-9]/g, '')
  const souple = propre.length >= 3 ? propre.replace(/[aeiouyc]/g, '_') : propre
  return `%${souple}%`
}

/** Vrai si chaque mot se retrouve dans l'un des textes, sans accents ni casse. */
export function correspond(textes: readonly (string | null | undefined)[], mots: readonly string[]): boolean {
  const tout = ` ${textes.map(t => normaliser(t ?? '')).join(' ')} `
  return mots.every(m => tout.includes(m))
}

/**
 * L'ordre des résultats : d'abord les enfants dont le prénom ou le nom
 * commence par le plus de mots cherchés (« léa fic » : Léa Fictif avant Cléa
 * Fictif), ensuite par prénom et nom.
 */
export function comparerResultats(mots: readonly string[]) {
  const debuts = (e: { prenom: string; nom: string }) => {
    const noms = `${normaliser(e.prenom)} ${normaliser(e.nom)}`.split(' ')
    return mots.filter(m => noms.some(x => x.startsWith(m))).length
  }
  return (a: { prenom: string; nom: string }, b: { prenom: string; nom: string }) =>
    debuts(b) - debuts(a) || a.prenom.localeCompare(b.prenom, 'fr') || a.nom.localeCompare(b.nom, 'fr')
}

/* ------------------------------------------------------------------------ */
/*  Ce qu'il faut savoir                                                     */
/* ------------------------------------------------------------------------ */

export type CleASavoir =
  | 'allergies' | 'regime' | 'medicaments' | 'sante' | 'activites' | 'eau' | 'besoins' | 'depart' | 'photo'

export interface LigneASavoir {
  cle: CleASavoir
  libelle: string
  texte: string
  /** Ce qui met l'enfant en danger ou engage l'équipe : en ambre. */
  alerte: boolean
}

export interface PersonneConnue {
  id: string
  first_name: string | null
  last_name: string | null
}

/**
 * Ce qu'il faut savoir d'un enfant, lu dans sa fiche — les questions du
 * parent, pas les anciennes colonnes. Les « tout va bien » et « RAS » sont
 * écartés comme dans les présences et la fiche santé PDF (`estSansImportance`).
 */
export function aSavoir(fiche: Fiche, personnes: readonly PersonneConnue[] = []): LigneASavoir[] {
  const { sante, vie, sorties, autorisations } = fiche
  const prenom = fiche.identite.prenom.trim() || 'L’enfant'
  const lignes: LigneASavoir[] = []
  const utile = (t: string) => !estSansImportance(t)

  if (sante.allergies) {
    const quoi = utile(sante.allergiesDetails) ? sante.allergiesDetails.trim() : 'oui'
    const consequences = utile(sante.allergiesConsequences) ? ` — ${sante.allergiesConsequences.trim()}` : ''
    lignes.push({ cle: 'allergies', libelle: 'Allergies', texte: `${quoi}${consequences}`, alerte: true })
  }
  if (sante.regimeSpecial) {
    lignes.push({ cle: 'regime', libelle: 'Régime', texte: utile(sante.regimeDetails) ? sante.regimeDetails.trim() : 'oui', alerte: false })
  }
  if (sante.medication) {
    const quoi = utile(sante.medicationDetails) ? sante.medicationDetails.trim() : 'oui'
    lignes.push({
      cle: 'medicaments',
      libelle: 'Médicaments',
      texte: `${quoi}${sante.medicationAutonomie ? ' (les prend seul·e)' : ''}`,
      alerte: true,
    })
  }
  if (utile(sante.infosMedicales)) {
    lignes.push({ cle: 'sante', libelle: 'Santé', texte: sante.infosMedicales.trim(), alerte: false })
  }
  if (!vie.participeATout) {
    lignes.push({
      cle: 'activites',
      libelle: 'Activités',
      texte: utile(vie.restrictions) ? `Limitées — ${vie.restrictions.trim()}` : 'Ne peut pas tout faire',
      alerte: true,
    })
  }
  const eau: string[] = []
  if (vie.natation === 'pas du tout' || vie.natation === 'difficilement') eau.push(PHRASE_NATATION[vie.natation](prenom))
  if (vie.peurDeLEau) eau.push('a peur de l’eau')
  if (eau.length > 0) lignes.push({ cle: 'eau', libelle: 'Eau', texte: eau.join(', '), alerte: true })
  if (vie.besoinsSpecifiques) {
    lignes.push({
      cle: 'besoins',
      libelle: 'Besoins',
      texte: utile(vie.situation) ? vie.situation.trim() : 'Besoins particuliers : voir la fiche',
      alerte: true,
    })
  }

  if (sorties.partSeul) {
    lignes.push({
      cle: 'depart',
      libelle: 'Départ',
      texte: `Repart seul·e${sorties.heureDepart ? ` à ${sorties.heureDepart}` : ''}`,
      alerte: true,
    })
  } else {
    const noms = sorties.personnesIds
      .map(id => personnes.find(p => p.id === id))
      .filter((p): p is PersonneConnue => !!p)
      .map(p => `${p.first_name ?? ''} ${p.last_name ?? ''}`.trim())
      .filter(Boolean)
    if (noms.length > 0) lignes.push({ cle: 'depart', libelle: 'Départ', texte: `avec ${noms.join(' ou ')}`, alerte: false })
  }

  /* Comme dans les présences : sans « oui » pour l'espace des familles, pas de photo. */
  if (autorisations.image.espacesPrives === null) {
    lignes.push({ cle: 'photo', libelle: 'Photo', texte: 'Pas de photo — le parent n’a pas encore répondu', alerte: true })
  } else if (autorisations.image.espacesPrives === 'non') {
    lignes.push({ cle: 'photo', libelle: 'Photo', texte: 'Pas de photo', alerte: true })
  }

  return lignes
}

export interface Signes {
  /** Allergie, régime, médicament ou information de santé. */
  sante: boolean
  besoins: boolean
  pasDePhoto: boolean
  /** Repart seul. */
  seul: boolean
}

export function signes(lignes: readonly LigneASavoir[]): Signes {
  const a = (cles: CleASavoir[]) => lignes.some(l => cles.includes(l.cle))
  return {
    sante: a(['allergies', 'regime', 'medicaments', 'sante']),
    besoins: a(['besoins', 'activites']),
    pasDePhoto: a(['photo']),
    seul: lignes.some(l => l.cle === 'depart' && l.alerte),
  }
}

/** Ce qui compte vraiment pour la liste « À savoir » : tout sauf la photo et un départ accompagné. */
export function aSignaler(lignes: readonly LigneASavoir[]): boolean {
  return lignes.some(l => l.cle !== 'photo' && !(l.cle === 'depart' && !l.alerte))
}

/* ------------------------------------------------------------------------ */
/*  Une semaine                                                              */
/* ------------------------------------------------------------------------ */

export interface EnfantDeLaSemaine {
  registrationId: string
  kidId: string
  prenom: string
  nom: string
  dateNaissance: string | null
  centre: string
  stage: string
  sousGroupe: string | null
}

export interface GroupeDeLaSemaine {
  cle: string
  titre: string
  sousTitre: string | null
  enfants: EnfantDeLaSemaine[]
}

/**
 * Les enfants de la semaine par groupe : centre (quand il y en a plusieurs),
 * stage, sous-groupe ; dans un groupe, par prénom. Un enfant inscrit à deux
 * sessions de la semaine paraît dans chacune.
 */
export function grouperLaSemaine(enfants: readonly EnfantDeLaSemaine[], plusieursCentres: boolean): GroupeDeLaSemaine[] {
  const groupes = new Map<string, GroupeDeLaSemaine>()
  for (const e of enfants) {
    const cle = `${plusieursCentres ? e.centre : ''}|${e.stage}|${e.sousGroupe ?? ''}`
    let g = groupes.get(cle)
    if (!g) {
      g = {
        cle,
        titre: plusieursCentres ? `${e.centre} · ${e.stage}` : e.stage,
        sousTitre: e.sousGroupe,
        enfants: [],
      }
      groupes.set(cle, g)
    }
    g.enfants.push(e)
  }
  const fr = (a: string, b: string) => a.localeCompare(b, 'fr', { numeric: true })
  for (const g of groupes.values()) g.enfants.sort((a, b) => fr(a.prenom, b.prenom) || fr(a.nom, b.nom))
  return [...groupes.values()].sort((a, b) => fr(a.titre, b.titre) || fr(a.sousTitre ?? '', b.sousTitre ?? ''))
}
